import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import Bolt from "@mui/icons-material/Bolt";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ScheduleSend from "@mui/icons-material/ScheduleSend";
import { Alert, Box, Collapse, type FormControlProps, Grid, Tooltip } from "@mui/material";
import { useDeepCompareMemo } from "@react-hookz/web";
import { type FC, type ReactElement, createElement, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useFlag } from "../../../server/optimizely";
import type { ISO8601_TIME } from "../../../utils/iso8601";
import { type UUID, uuidv4 } from "../../../utils/uuid";
import { useAccountLimitsContext } from "../../context/account-limit-context";
import { listenEnabled } from "../../generic/server/supabase-account-service";
import { Tile } from "../../generic/components/tile/tile";
import type { ConnectionCategory } from "../../moment/hooks/use-connections";
import type { ProgramsState } from "../../moment/hooks/use-programs";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { useToTarget } from "../../to/use-to-target";
import { OneOffForm } from "./one-off-form";
import { ScheduleForm } from "./schedule-form";
import { SurveyCompletedForm } from "./survey-completed-form";

export type ScheduleType = "scheduled" | "one-off" | "immediate" | "survey-completed";

type SchedulingOption = {
  scheduleType: ScheduleType;
  disabled: boolean | undefined;
  onChange: (type: ScheduleType) => void;
};

/** Radio for send scheduled vs one-off vs immediate. */
export const ScheduleEnabler: FC<ScheduleEnablerProps> = ({
  initialScheduleType,
  disabled,
  setIcon,
  reloadMergeTags,
  showValidationErrors,
  programs,
  connectionCategories,
  restrictedSegments = [],
}) => {
  // This flag is a long term flag for CS.
  const [connection_immediate_send] = useFlag("connection_immediate_send");
  const { setValue, control, getValues } = useFormContext<MomentFormMoment>();

  useWatch({
    control,
    name: ["cc_bcc", "immediate", "schedule.event"],
  });
  const [cc_bcc, immediate, calendar] = getValues([
    "cc_bcc",
    "immediate",
    "schedule.event.calendar",
  ]);

  const showWarning = useDeepCompareMemo(() => {
    const cc = cc_bcc?.cc ?? [];
    const bcc = cc_bcc?.bcc ?? [];
    return (cc?.length > 0 || bcc?.length > 0) && (immediate || calendar);
  }, [cc_bcc, immediate, calendar]);

  const { isManagerConnection } = useToTarget();
  const [_scheduleType, _setScheduleType] = useState<ScheduleType>(initialScheduleType);

  const { listen_entitlement } = useAccountLimitsContext();
  const [surveyCompletedFlag] = useFlag("cha-4340-survey-completed-trigger");

  const [channel, schedule] = useWatch({ control, name: ["channel", "schedule"] });

  const SURVEY_COMPLETED_CHANNELS = ["slack", "teams", "work_email", "personal_email"] as const;
  const channelSupportsSurvey =
    !channel ||
    SURVEY_COMPLETED_CHANNELS.includes(channel as (typeof SURVEY_COMPLETED_CHANNELS)[number]);
  const canSelectSurvey =
    surveyCompletedFlag && listenEnabled(listen_entitlement) && channelSupportsSurvey;

  // When the channel changes to one that doesn't support survey-completed, fall back to scheduled
  const scheduleType: ScheduleType =
    _scheduleType === "survey-completed" && !channelSupportsSurvey ? "scheduled" : _scheduleType;

  const showSurveyTile = canSelectSurvey || scheduleType === "survey-completed";

  const setType = useCallback(
    (type: ScheduleType) => {
      const icons = {
        immediate: Bolt,
        "one-off": ScheduleSend,
        scheduled: CalendarMonth,
        "survey-completed": FactCheckOutlined,
      };
      setIcon?.(
        createElement(icons[type], {
          sx: {
            height: "45px",
            width: "45px",
            mb: 0.5,
          },
        }),
      );
      _setScheduleType(type);
    },
    [setIcon, _setScheduleType],
  );

  useEffect(() => {
    if (_scheduleType === "survey-completed" && !channelSupportsSurvey) {
      setValue("trigger_survey_id", null, { shouldDirty: true });
      setValue("immediate", false, { shouldDirty: true });
      setValue(
        "schedule",
        {
          id: uuidv4(),
          send_at: "12:00" as ISO8601_TIME,
          event_initial: true,
          event_repeat: false,
          on_event_key: null,
          dow: {
            sunday: 0,
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
          },
          interval_json: null,
          event: null,
        },
        { shouldDirty: true },
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setType("scheduled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const onChange = (type: ScheduleType): void => {
    // If scheduleType and value are the same, nothing has changed
    if (type === scheduleType) return;

    if (type === "survey-completed") {
      setValue("immediate", false, { shouldDirty: true });
      setValue("schedule", null, { shouldDirty: true });
    } else {
      setValue("trigger_survey_id", null, { shouldDirty: true });
      setValue("immediate", type === "immediate", { shouldDirty: true });
      if (type === "immediate") {
        setValue("schedule", null, { shouldDirty: true });
      } else {
        setValue(
          "schedule",
          {
            id: uuidv4(),
            send_at: "12:00" as ISO8601_TIME,
            event_initial: true,
            event_repeat: false,
            on_event_key: null,
            dow: {
              sunday: 0,
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
            },
            interval_json: null,
            event: null,
          },
          { shouldDirty: true },
        );
      }
    }

    setType(type);
  };

  return (
    <>
      <Grid
        sx={{ pl: 1 }}
        display="flex"
        flexDirection="row"
        alignItems="center"
        spacing={2}
        container
      >
        <ScheduleSendOption scheduleType={scheduleType} disabled={disabled} onChange={onChange} />
        {(!isManagerConnection || connection_immediate_send) && (
          <>
            <OneOffSendOption scheduleType={scheduleType} disabled={disabled} onChange={onChange} />
            <ImmediateSendOption
              scheduleType={scheduleType}
              disabled={disabled}
              onChange={onChange}
            />
          </>
        )}
        {showSurveyTile && (
          <SurveyCompletedOption
            scheduleType={scheduleType}
            disabled={disabled}
            onChange={onChange}
          />
        )}
      </Grid>
      <Grid>
        {showWarning ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            This may send a lot of messages to CC & BCC recipients when the Communication is sent.
          </Alert>
        ) : null}
        <Collapse in={scheduleType !== "immediate"} sx={{ mt: 2 }}>
          {scheduleType === "scheduled" && schedule !== null && (
            <ScheduleForm
              reloadMergeTags={reloadMergeTags}
              disabled={disabled ?? false}
              showValidationErrors={showValidationErrors}
            />
          )}
          {scheduleType === "one-off" && (
            <OneOffForm disabled={disabled ?? false} showValidationErrors={showValidationErrors} />
          )}
          {scheduleType === "survey-completed" && (
            <SurveyCompletedForm
              disabled={disabled ?? false}
              showValidationErrors={showValidationErrors}
              reloadMergeTags={reloadMergeTags}
              programs={programs}
              connectionCategories={connectionCategories}
              restrictedSegments={restrictedSegments}
            />
          )}
        </Collapse>
      </Grid>
    </>
  );
};

const ScheduleSendOption: FC<SchedulingOption> = ({ scheduleType, disabled, onChange }) => {
  return (
    <Tooltip title="Schedule send">
      <Grid>
        <Tile
          name="when-schedule"
          onClick={() => onChange("scheduled")}
          selected={scheduleType === "scheduled"}
          disabled={disabled}
        >
          <Box>
            <CalendarMonth
              sx={{
                height: "105px",
                width: "105px",
              }}
            />
          </Box>
        </Tile>
      </Grid>
    </Tooltip>
  );
};

const OneOffSendOption: FC<SchedulingOption> = ({ scheduleType, disabled, onChange }) => {
  return (
    <Tooltip title="Send one-off">
      <Grid>
        <Tile
          name="when-one-off"
          onClick={() => onChange("one-off")}
          selected={scheduleType === "one-off"}
          disabled={disabled}
        >
          <ScheduleSend
            sx={{
              height: "105px",
              width: "105px",
            }}
          />
        </Tile>
      </Grid>
    </Tooltip>
  );
};

const ImmediateSendOption: FC<SchedulingOption> = ({ scheduleType, disabled, onChange }) => {
  return (
    <Tooltip title="Send immediately">
      <Grid>
        <Tile
          name="when-immediately"
          onClick={() => onChange("immediate")}
          selected={scheduleType === "immediate"}
          disabled={disabled}
        >
          <Bolt
            sx={{
              height: "105px",
              width: "105px",
            }}
          />
        </Tile>
      </Grid>
    </Tooltip>
  );
};

const SurveyCompletedOption: FC<SchedulingOption> = ({ scheduleType, disabled, onChange }) => {
  return (
    <Tooltip title="Survey completed">
      <Grid>
        <Tile
          name="when-survey-completed"
          onClick={() => onChange("survey-completed")}
          selected={scheduleType === "survey-completed"}
          disabled={disabled}
        >
          <FactCheckOutlined sx={{ height: "105px", width: "105px" }} />
        </Tile>
      </Grid>
    </Tooltip>
  );
};

export type ScheduleEnablerProps = FormControlProps & {
  initialScheduleType: ScheduleType;
  setIcon?: (icon: ReactElement) => void;
  reloadMergeTags: () => void;
  showValidationErrors: boolean;
  programs: ProgramsState;
  connectionCategories: ConnectionCategory[];
  restrictedSegments?: UUID[];
};
